import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiQueryPromise, ViewEnum } from '@/types';
import { getResponseJwtHeader } from '@/utils/web';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import queryKeys from '@/constants/query-keys';
import { useAccountInfo } from '@/hooks/query/account';
import { useBetslipStore } from '@/store/betslip';
import { useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import { BetslipService } from '@/services/betslip.service';
import { uiService } from '@/services/ui.service';
import { isDesktop } from 'react-device-detect';
import { useSiteStore } from '@/store/site';
import { useAppearanceStore } from '@/store/appearance';
import { getAuthToken, useAuthActions } from '@/store/auth';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import { UserInfo } from '@nf/types/account';
import { useRouter } from 'next/navigation';
import { WebOddsType } from '@nf/utils-common/constants';
import { rumLogger } from '@/features/providers/openobserve-provider';
import type {
	AcceptTicketStatus,
	BetTicketsResponse,
	ParlayTicketModel,
	ParlayTicketStatus,
	ParlayTicketsModel,
	SyncTicketsResponse,
	TicketModel,
	TicketStatus,
	VoucherType
} from './types';
import { BetAction } from './types';

/** 呼叫 API - GetComboTickets */
const syncTicket = async (tickets: Array<TicketModel>, useVoucher: VoucherType, useSabaCoin?: boolean): Promise<ApiQueryPromise<SyncTicketsResponse>> => {
	const authToken = getAuthToken();
	const { ctcdDomain } = useSiteStore.getState().apiDomain;

	let formData = new FormData();

	formData = BetslipService.convertTicketToFormData(formData, tickets, 'ItemList');

	if (tickets.length == 1 && useVoucher != 'None') {
		if (useVoucher == 'OddsBooster') {
			formData.append('rewardSystemVoucherType', '1');
			formData.append('leagueGroupId', tickets[0].LeagueGroupId.toString());
		} else if (useVoucher == 'RiskFreeBet') {
			formData.append('rewardSystemVoucherType', '2');
		} else if (useVoucher === 'OddsBooster-promo') {
			formData.append('rewardSystemVoucherType', '7');
		} else if (useVoucher === 'NewRiskFreeBet') {
			formData.append('rewardSystemVoucherType', '10');
		} else if (useVoucher === 'AffPromotionOddsBoost') {
			formData.append('rewardSystemVoucherType', '11');
		} else if (useVoucher === 'GiftRiskFreeBet') {
			formData.append('leagueId', tickets[0].LeagueId.toString());
			formData.append('rewardSystemVoucherType', '14');
		} else if (useVoucher === 'FreeBet') {
			formData.append('rewardSystemVoucherType', '15');
			const freeBetVoucherId = useBetslipStore.getState().selectedFreeBetVoucherId;
			formData.append('leagueId', tickets[0].LeagueId.toString());
			if (freeBetVoucherId != null) {
				formData.append('voucherId', freeBetVoucherId);
				formData.append(
					'voucherSystemVersion',
					useBetslipStore.getState().selectedFreeBetVoucherSystemVersion.toString()
				);
			}
		}
	}
	if (useSabaCoin) {
		formData.append('isUseSabaCoin', 'true');
	}

	const options = {
		body: formData
	};

	const response = await fetchComposer.postWithBearerBase(cloudUrlParser(`${ctcdDomain}/api/Betting/GetComboTickets`), options)(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

/** 呼叫 API - DoComboTicketsBet */
const betTicket = async (
	tickets: Array<TicketModel>,
	parlayData: ParlayTicketsModel | null,
	user: UserInfo | null | undefined,
	useVoucher: VoucherType,
	isQuickBet: boolean,
	useSabaCoin: boolean,
	oddsPromotionCode?: string
): Promise<ApiQueryPromise<BetTicketsResponse>> => {
	const authToken = getAuthToken();
	const { apiDomain: { ctcdDomain }, isParlayMode } = useSiteStore.getState();
	const { view } = useAppearanceStore.getState();
	const { betAction, betslipTab, useFreeRiskFreeVoucher } = useBetslipStore.getState();
	const canBetSingle = (user?.UserSetting?.BettingMode === 0 || betslipTab === 'single') || tickets.length === 1;
	const canBetParlay = (user?.UserSetting?.BettingMode === 0 || betslipTab === 'parlay') && parlayData?.Tickets?.length;
	let formData = new FormData();

	const bettingTickets = tickets.filter(ticket => ticket.stake);
	
	// 從 ticket 中取得實際的 oddsType
	const ticketOddsType = bettingTickets[0]?.OddsType;
	// 判斷ticket oddsType 是否與 user oddsType 相同，不同表示freebet 轉換過盤別

	const ticketPriceType = bettingTickets[0]?.PriceType;

	const isFreeBetChangeOddsType = 
		useVoucher === 'FreeBet' && 
		user?.OType != null &&
		(
			ticketOddsType != null && ticketOddsType !== user?.OType || 
			(ticketPriceType === WebOddsType.DEC && user?.OType !== WebOddsType.DEC)
		);

	let bettingParlayTickets: ParlayTicketModel[] = [];

	if (parlayData?.Tickets?.length) {
		bettingParlayTickets = parlayData.comboData?.filter(ticket => ticket.CanBet && ticket.stake);
	}

	// // 特殊邏輯：分離模式下僅有mix parlay有stake的情況下, 要無視tab替該mix parlay下注
	// if (bettingTickets.length === 0 && bettingParlayTickets?.length === 1 && bettingParlayTickets[0].isMixParlay === true) {
	// 	canBetParlay = true;
	// }

	if (canBetSingle) {
		formData = BetslipService.convertTicketToFormData(formData, bettingTickets, 'ticketData[ItemList]');
	}

	if (canBetParlay && bettingParlayTickets.length) {
		formData = BetslipService.convertTicketToFormData(formData, parlayData?.Tickets ?? [], 'comboData[ItemList]');
		bettingParlayTickets?.forEach((parlayTicket, index) => {
			formData.append(`comboData[ComboLists][${index}][Type]`, parlayTicket.Type.toString());
			formData.append(`comboData[ComboLists][${index}][BetCount]`, parlayTicket.BetCount.toString());
			formData.append(`comboData[ComboLists][${index}][Stake]`, parlayTicket.stake.toString());
		});
	}

	// accept better odds
	formData.append('acceptBetterOdds', (user?.BetterOdds ?? false).toString());

	// reward
	if (bettingTickets.length == 1 && useVoucher != 'None') {
		if (useVoucher == 'OddsBooster') {
			formData.append('rewardSystemVoucherType', '1');
			formData.append('leagueGroupId', tickets[0].LeagueGroupId.toString());
		} else if (useVoucher == 'RiskFreeBet') {
			formData.append('rewardSystemVoucherType', '2');
		} else if (useVoucher === 'OddsBooster-promo') {
			formData.append('rewardSystemVoucherType', '7');
		} else if (useVoucher === 'NewRiskFreeBet') {
			formData.append('rewardSystemVoucherType', '10');
		} else if (useVoucher === 'AffPromotionOddsBoost') {
			formData.append('rewardSystemVoucherType', '11');
		} else if (useVoucher === 'GiftRiskFreeBet') {
			formData.append('leagueId', tickets[0].LeagueId.toString());
			formData.append('rewardSystemVoucherType', '14');
		} else if (useVoucher === 'FreeBet') {
			formData.append('rewardSystemVoucherType', '15');
			formData.append('leagueId', tickets[0].LeagueId.toString());

			const freeBetVoucherId = useBetslipStore.getState().selectedFreeBetVoucherId;
			if (freeBetVoucherId != null) {
				formData.append('voucherId', freeBetVoucherId);
				formData.append(
					'voucherSystemVersion',
					useBetslipStore.getState().selectedFreeBetVoucherSystemVersion.toString()
				);
			}
			if (isFreeBetChangeOddsType) {
				const oddsTypeToUse = ticketPriceType === WebOddsType.DEC ? ticketPriceType : ticketOddsType;
				
				if (oddsTypeToUse != null) {
					formData.append('oddsType', oddsTypeToUse.toString());
				}
			}
		}

		if (['OddsBooster', 'OddsBooster-promo', 'AffPromotionOddsBoost'].includes(useVoucher)) {
			formData.append('oddsBeforeRecommendOddsBoost', (bettingTickets[0]?.OddsBeforeOddsBoost ?? 0).toString());
			// formData.append('oddsBeforeRecommendOddsBoost', '1.00');
			formData.append('oddsBoost', (bettingTickets[0]?.OddsBoost ?? 0).toString());

			if (oddsPromotionCode) {
				formData.append('OddsPromotionCode', oddsPromotionCode);
			}
		}
	}

	if (useFreeRiskFreeVoucher) {
		formData.append('rewardSystemVoucherType', '6');
	}

	const uuidValue = uuid();
	formData.append('BettingId', uuidValue);

	const hasSBR = tickets?.some?.(ticket => ticket.isSBR);
	const betAgainFlag = betAction === BetAction.BetAgain ? BetAction.BetAgain : 0;
	const betActionValue = hasSBR
		? BetAction.SBRWidget | betAgainFlag
		: (isQuickBet ? BetAction.QuickBet | betAgainFlag : betAction);

	formData.append('BetAction', betActionValue.toString());

	if (useSabaCoin) {
		formData.append('isUseSabaCoin', 'true');
	}
	const options = {
		body: formData
	};

	const response = await fetchComposer.postWithBearerBase(cloudUrlParser(`${ctcdDomain}/api/Betting/DoComboTicketsBet`), options)(authToken);

	if (!response.ok) {
		rumLogger.error('Bet submit failed', { bettingId: uuidValue, status: response.status });
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	if (view === ViewEnum.mars && canBetParlay && bettingParlayTickets.length) {
		BetslipService.setMarsParlayBetKafka(view, isParlayMode);
	}

	const responseData = await response.json();

	rumLogger.info('Bet submitted', {
		bettingId: uuidValue,
		ticketCount: bettingTickets.length,
		parlayCount: bettingParlayTickets?.length ?? 0,
		betAction: betActionValue,
		errorCode: responseData?.Data?.MultiData?.ItemList?.[0]?.ErrorCode,
	});

	return {
		data: responseData,
		jwtToken: getResponseJwtHeader(response)
	};
}

/** 呼叫 API - GetStatusByTransidJson */
const getSingleStatus = async (transId: string): Promise<ApiQueryPromise<TicketStatus>> => {
	const { ctcdDomain } = useSiteStore.getState().apiDomain;
	const authToken = getAuthToken();

	const response =
		await fetchComposer.getWithBearer(`${ctcdDomain}/Statement/GetStatusByTransidJson?transid=${transId}`)(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

/** 呼叫 API - GetParlayStatusByTransidJson */
const getParlayStatus = async (transId: string): Promise<ApiQueryPromise<ParlayTicketStatus>> => {
	const { ctcdDomain } = useSiteStore.getState().apiDomain;
	const authToken = getAuthToken();

	const response =
		await fetchComposer.getWithBearer(`${ctcdDomain}/Statement/GetParlayStatusByTransidJson?transid=${transId}`)(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useTicketStatus = (transId: string) => {
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.TICKET_STATUS + transId],
		queryFn: () => getSingleStatus(transId),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: false
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};

		updateJwt();
	}, [data?.jwtToken]);

	return {
		ticketStatus: data?.data.Data,
		isLoading,
		isError,
		error,
		refetch
	};
}

export const useParlayStatus = (transId: string) => {
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.PARLAY_STATUS + transId],
		queryFn: () => getParlayStatus(transId),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: false
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};

		updateJwt();
	}, [data?.jwtToken]);

	return {
		parlayStatus: data?.data.Data,
		isLoading,
		isError,
		error,
		refetch
	};
}

export const useTicketInfo = (tickets: Array<TicketModel>) => {
	const [useVoucher, useSabaCoin] = useBetslipStore(state => [state.useVoucher, state.useSabaCoin]);

	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.TICKET_INFO + tickets.length],
		queryFn: () => syncTicket(tickets, useVoucher, useSabaCoin),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: false
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};

		updateJwt();
	}, [data?.jwtToken]);

	return {
		syncTicketResponse: data?.data.Data,
		isLoading,
		isError,
		error,
		refetch
	};
};

export const useTicketBet = () => {
	const { user } = useAccountInfo();
	const router = useRouter();

	const [
		currentMultiTicketModel,
		currentParlayTicketModel,
		useVoucher,
		useSabaCoin,
		oddsPromotionCode
	] = useBetslipStore(state => [
		state.ticketCarts['normal'].multiTickets,
		state.ticketCarts['normal'].parlayTicketModel,
		state.useVoucher,
		state.useSabaCoin,
		state.ticketCarts['normal'].oddsPromotionCode
	]);

	const { data, isFetching, isError, error, refetch } = useQuery({
		queryKey: [`${queryKeys.TICKET_BET}_${currentMultiTicketModel.length}_${currentParlayTicketModel?.Tickets?.length}`],
		queryFn: () => betTicket(currentMultiTicketModel, currentParlayTicketModel, user, useVoucher, false, useSabaCoin, oddsPromotionCode),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: false,
	});
	const { updateAuthToken } = useAuthActions();

	const errorData = data?.data 
		? {
			errorCode: data.data.ErrorCode,
			errorMessage: data.data.ErrorMsg
		} 
		: null;

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};
		updateJwt();
	}, [data?.jwtToken]);

	useEffect(() => {
		if (String(errorData?.errorCode).includes('IP-0001')) {
			router.push(`/app-error?errorcode=${errorData?.errorCode}`);
		}
	}, [errorData?.errorCode, router]);

	return {
		betTicketResponse: data?.data.Data,
		isError,
		isFetching,
		error,
		errorData,
		refetch
	};
};

export const useQuickBet = (oddsKey: string, setOddsKey: (oddsKey: string) => void) => {
	const queryClient = useQueryClient();
	const { user } = useAccountInfo();
	const { updateAuthToken } = useAuthActions();

	const quickBetStake = user?.SportsQuickBetStake as number;

	//const [isShowedMessage, setIsShowedMessage] = useState(false);

	const [
		quickBetTicket,
		clearQuickBetTicket,
		oddsPromotionCode
	] = useBetslipStore(state => [
		state.quickBetTicket,
		state.clearQuickBetTicket,
		state.ticketCarts['normal'].oddsPromotionCode
	]);

	let ticketModel = {
		...quickBetTicket
	} as TicketModel;

	if (ticketModel.key == oddsKey && quickBetStake <= 0) {
		uiService.processQuickBetButton('');
		document.dispatchEvent(new CustomEvent('show-toast-msg', {
			detail: { resourceKey: 'lbl_SetQuickBetStake' }
		}));
		clearQuickBetTicket();
		setOddsKey('');
	}

	const { data: getTicketResponse, isFetching: getTicketFetching, refetch } = useQuery({
		queryKey: [queryKeys.TICKET_INFO, oddsKey],
		queryFn: () => syncTicket([ticketModel], 'None'),
		cacheTime: 0,
		staleTime: 0,
		refetchOnWindowFocus: false,
		enabled: ticketModel.key == oddsKey && quickBetStake > 0
	});

	useEffect(() => {
		const updateJwt = () => {
			if (getTicketResponse?.jwtToken) {
				updateAuthToken(getTicketResponse.jwtToken);
			}
		};
		updateJwt();
	}, [getTicketResponse?.jwtToken]);

	if (getTicketResponse?.data) {

		const dbTicket = getTicketResponse.data.Data?.MultiData?.[0];
		ticketModel = BetslipService.patchTicketModel(ticketModel, (dbTicket ?? {} as TicketModel));

		ticketModel.stake = quickBetStake;

		// if (ticketModel.stake < ticketModel.minBet) {
		// 	ticketModel.stake = ticketModel.minBet;
		// 	if (!isShowedMessage) {
		// 		setToastMsg(`Lower than min bet. Your stake is adjusted to ${ticketModel.minBet} now.`);
		// 		setIsShowedMessage(true);
		// 	}
		// } else if (ticketModel.stake > ticketModel.maxBet) {
		// 	ticketModel.stake = ticketModel.maxBet;
		// 	if (!isShowedMessage) {
		// 		setToastMsg(`Higher than max bet. Your stake is adjusted to ${ticketModel.maxBet} now.`);
		// 		setIsShowedMessage(true);
		// 	}
		// }
	}

	const { data: doBetResponse, isFetching: doBetFetching } = useQuery({
		queryKey: [queryKeys.TICKET_BET, oddsKey, ticketModel?.TicketTime],
		queryFn: () => betTicket([ticketModel], null, user, 'None', true, false, oddsPromotionCode),
		cacheTime: 0,
		staleTime: 0,
		refetchOnWindowFocus: false,
		enabled: !!ticketModel?.sync && !!ticketModel?.OddsID && oddsKey == ticketModel?.key
	});

	useEffect(() => {
		const updateJwt = () => {
			if (doBetResponse?.jwtToken) {
				updateAuthToken(doBetResponse.jwtToken);
			}
		};
		updateJwt();
	}, [doBetResponse?.jwtToken]);

	useEffect(() => {
		const multiTickets = doBetResponse?.data.Data?.MultiData.ItemList;
		if (multiTickets && ticketModel?.key == oddsKey) {
			document.dispatchEvent(new CustomEvent('show-toast-msg', {
				detail: { msg: multiTickets[0].Message as string }
			}));
		}

		clearQuickBetTicket();
		setOddsKey('');

		// refetch my bets
		if (isDesktop && doBetResponse?.data) {
			queryClient.invalidateQueries({
				queryKey: [queryKeys.UNSETTLED_CATEGORY_INFO],
			});
			queryClient.invalidateQueries({
				queryKey: [queryKeys.UNSETTLED_STATUS_COUNT_INFO]
			});
		} else if (doBetResponse?.data) {
			queryClient.invalidateQueries({
				queryKey: [queryKeys.UNSETTLED_STATUS_COUNT_INFO]
			});
		}
	}, [doBetResponse?.data]);

	const betData = doBetResponse?.data.Data;
	const itemList = betData?.MultiData.ItemList[0];
	const transId = itemList?.UnderOverTicket?.TransID;

	const errorCode = doBetResponse?.data.Data?.MultiData.ItemList[0].ErrorCode;
	const checkWaitingTicket = doBetResponse?.data.Data?.MultiData.ItemList[0].CheckWaitingTicket;

	const status: AcceptTicketStatus = errorCode != 0 ? 'reject' :
		checkWaitingTicket ? 'waiting' : 'running';

	return {
		status: betData ? status : null,
		ticketId: status === 'running' && transId !== undefined 
			? transId.toString() 
			: undefined,
		isLoading: getTicketFetching || doBetFetching,
		doQuickBet: refetch
	}
}


